import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Timer, AlertCircle, RefreshCw, Loader2, ChevronRight } from 'lucide-react';
import { useFactory } from '../../../context/FactoryContext';
import { useCurrentEmployee } from '../../../hooks/useCurrentEmployee';
import { usePermissions } from '../../../hooks/usePermissions';
import { isLaborRestrictedViewer, isProdSupervisorTeamScoped } from '../../../utils/roleChecks';
import { filterTeamsByRole, collectTeamEmployeeIds } from '../../../utils/teamScope';
import { employeeService } from '../empleados/services/employeeService';
import { teamService } from '../empleados/services/teamService';
import {
  resolveLaborFactoryId,
  getEmployeeDisplayName,
} from '../calendario/utils/laborFactoryUtils';
import { laborHoursService } from './services/laborHoursService';
import { buildMonthlyRows, formatHours, formatCellDays, normalizeYearTotals } from './utils/laborHoursUtils';
import { ControlHorasMonthDetail } from './components/ControlHorasMonthDetail';

const KPI = ({ label, value, sub }) => (
  <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
    <p className="text-xs text-gray-400 mb-1">{label}</p>
    <p className="text-2xl font-semibold text-white">{value}</p>
    {sub ? <p className="text-xs text-gray-500 mt-1">{sub}</p> : null}
  </div>
);

const ControlHoras = () => {
  const { selectedFactory, factories } = useFactory();
  const queryClient = useQueryClient();
  const currentYear = new Date().getFullYear();
  const [searchParams, setSearchParams] = useSearchParams();
  const { roleKeys, isLoading: permissionsLoading } = usePermissions();
  const isViewer = useMemo(() => isLaborRestrictedViewer(roleKeys), [roleKeys]);
  const isSupervisorScoped = useMemo(() => isProdSupervisorTeamScoped(roleKeys), [roleKeys]);
  const { employeeId: currentEmployeeId, employeeName: currentEmployeeName, isReady: currentEmployeeReady } =
    useCurrentEmployee();

  const [year, setYear] = useState(() => {
    const y = Number(searchParams.get('year'));
    return y >= 2000 && y <= 2100 ? y : currentYear;
  });
  const [employeeId, setEmployeeId] = useState(() => searchParams.get('employee') || '');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const m = Number(searchParams.get('month'));
    return m >= 1 && m <= 12 ? m : null;
  });
  const [actionError, setActionError] = useState(null);

  const factoryId = useMemo(
    () => resolveLaborFactoryId(selectedFactory, factories),
    [selectedFactory, factories]
  );
  const factoryBase = selectedFactory || '';

  const syncUrl = (nextEmployee, nextYear, nextMonth) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (nextEmployee) next.set('employee', String(nextEmployee));
        else next.delete('employee');
        if (nextYear) next.set('year', String(nextYear));
        else next.delete('year');
        if (nextMonth >= 1 && nextMonth <= 12) next.set('month', String(nextMonth));
        else next.delete('month');
        return next;
      },
      { replace: true }
    );
  };

  const { data: supervisorTeams = [], isLoading: loadingSupervisorTeams } = useQuery({
    queryKey: ['labor-teams-hours-scope', factoryId, currentEmployeeId],
    queryFn: () => teamService.getTeams(),
    enabled: Boolean(factoryId) && isSupervisorScoped && currentEmployeeReady,
    staleTime: 60_000,
    select: (teams) =>
      filterTeamsByRole(teams, {
        factoryId,
        roleKeys,
        leaderEmployeeId: currentEmployeeId,
      }),
  });

  const supervisorTeam = supervisorTeams[0] ?? null;

  const { data: supervisorTeamMembers = [] } = useQuery({
    queryKey: ['labor-team-members-hours', supervisorTeam?.id],
    queryFn: () => teamService.getTeamMembers(supervisorTeam.id),
    enabled: Boolean(supervisorTeam?.id),
  });

  const teamEmployeeIds = useMemo(() => {
    if (!isSupervisorScoped) return null;
    return collectTeamEmployeeIds(supervisorTeam, supervisorTeamMembers);
  }, [isSupervisorScoped, supervisorTeam, supervisorTeamMembers]);

  useEffect(() => {
    if (isViewer && currentEmployeeReady && currentEmployeeId) {
      setEmployeeId(String(currentEmployeeId));
      syncUrl(currentEmployeeId, year, selectedMonth);
    }
    // Solo se ejecuta al resolver el empleado actual cuando es viewer
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isViewer, currentEmployeeReady, currentEmployeeId]);

  useEffect(() => {
    if (!isSupervisorScoped || !teamEmployeeIds?.size) return;
    if (employeeId && !teamEmployeeIds.has(Number(employeeId))) {
      setEmployeeId('');
      setSelectedMonth(null);
      syncUrl('', year, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSupervisorScoped, teamEmployeeIds, employeeId]);

  const canQueryEmployeeHours = useMemo(() => {
    if (!employeeId) return false;
    if (!isSupervisorScoped) return true;
    return Boolean(teamEmployeeIds?.has(Number(employeeId)));
  }, [employeeId, isSupervisorScoped, teamEmployeeIds]);

  const { data: employees = [], isLoading: loadingEmployees } = useQuery({
    queryKey: ['labor-hours-employees', factoryBase],
    queryFn: () => employeeService.getEmployees(factoryBase, true, null),
    enabled: Boolean(factoryBase) && !isViewer,
    staleTime: 60_000,
  });

  const {
    data: summary,
    isLoading: loadingSummary,
    isFetching: fetchingSummary,
    error: queryError,
  } = useQuery({
    queryKey: ['labor-hours-year', employeeId, year],
    queryFn: () => laborHoursService.getEmployeeYearHours(employeeId, year),
    enabled: canQueryEmployeeHours,
  });

  const {
    data: monthDetail,
    isLoading: loadingMonthDetail,
    error: monthDetailError,
  } = useQuery({
    queryKey: ['labor-hours-month-detail', employeeId, year, selectedMonth],
    queryFn: () => laborHoursService.getEmployeeMonthDetail(employeeId, year, selectedMonth),
    enabled: canQueryEmployeeHours && selectedMonth != null,
  });

  const generateMutation = useMutation({
    mutationFn: () => laborHoursService.generateEmployeeYearHours(employeeId, year),
    onSuccess: (data) => {
      queryClient.setQueryData(['labor-hours-year', employeeId, year], data);
      if (selectedMonth != null) {
        queryClient.invalidateQueries({
          queryKey: ['labor-hours-month-detail', employeeId, year, selectedMonth],
        });
      }
      setActionError(null);
    },
    onError: (err) => {
      setActionError(err?.message || 'No se pudieron regenerar los resúmenes.');
    },
  });

  const prevFactoryIdRef = useRef(factoryId);

  useEffect(() => {
    if (prevFactoryIdRef.current === factoryId) return;
    prevFactoryIdRef.current = factoryId;

    setEmployeeId('');
    setSelectedMonth(null);
    setActionError(null);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('employee');
        next.delete('year');
        next.delete('month');
        return next;
      },
      { replace: true }
    );
  }, [factoryId, setSearchParams]);

  const sortedEmployees = useMemo(
    () =>
      [...employees].sort((a, b) =>
        getEmployeeDisplayName(a).localeCompare(getEmployeeDisplayName(b), 'es')
      ),
    [employees]
  );

  const selectableEmployees = useMemo(() => {
    if (!isSupervisorScoped) return sortedEmployees;
    if (!teamEmployeeIds?.size) return [];
    return sortedEmployees.filter((emp) => teamEmployeeIds.has(Number(emp.id)));
  }, [sortedEmployees, isSupervisorScoped, teamEmployeeIds]);

  const selectedEmployee = useMemo(
    () => employees.find((e) => String(e.id) === String(employeeId)),
    [employees, employeeId]
  );

  const monthlyRows = useMemo(() => buildMonthlyRows(summary), [summary]);
  const totals = useMemo(
    () => normalizeYearTotals(summary?.totals ?? summary?.Totals),
    [summary]
  );
  const missingMonths = summary?.missing_months || [];
  const hasMissingMonths = missingMonths.length > 0;

  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = currentYear + 1; y >= currentYear - 4; y--) {
      years.push(y);
    }
    return years;
  }, [currentYear]);

  const handleEmployeeChange = (id) => {
    setEmployeeId(id);
    setSelectedMonth(null);
    syncUrl(id, year, null);
  };

  const handleYearChange = (y) => {
    setYear(y);
    setSelectedMonth(null);
    syncUrl(employeeId, y, null);
  };

  const handleMonthSelect = (month) => {
    setSelectedMonth(month);
    syncUrl(employeeId, year, month);
  };

  const handleMonthBack = () => {
    setSelectedMonth(null);
    syncUrl(employeeId, year, null);
  };

  if (permissionsLoading) return null;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Timer className="w-8 h-8 text-blue-400" />
        <div>
          <h1 className="text-2xl font-bold text-white">Control de horas</h1>
          <p className="text-sm text-gray-400">
            {isSupervisorScoped
              ? 'Consulta el resumen anual de horas de los integrantes de tu equipo. Pulsa un mes para ver el detalle semanal.'
              : 'Resumen anual de días laborables, vacaciones, ausencias y horas teóricas vs reales. Los resúmenes se generan automáticamente el día 2 de cada mes; puedes regenerarlos manualmente si lo necesitas.'}
          </p>
        </div>
      </div>

      {!factoryId && (
        <div className="mb-4 flex items-center gap-2 text-amber-400 bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>Selecciona una fábrica en el header para consultar el control de horas.</span>
        </div>
      )}

      {isSupervisorScoped && factoryId && !loadingSupervisorTeams && !supervisorTeam && (
        <div className="mb-4 flex items-center gap-2 text-amber-400 bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>No tienes ningún equipo asignado como líder.</span>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div className="min-w-[220px]">
          <label htmlFor="hours-employee" className="block text-xs text-gray-400 mb-1">
            {isSupervisorScoped ? 'Compañero de equipo' : 'Empleado'}
          </label>
          {isViewer ? (
            <div className="w-full bg-gray-700/50 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm">
              {currentEmployeeName || '—'}
            </div>
          ) : (
            <select
              id="hours-employee"
              value={employeeId}
              onChange={(e) => handleEmployeeChange(e.target.value)}
              disabled={
                !factoryId ||
                loadingEmployees ||
                (isSupervisorScoped && (loadingSupervisorTeams || !supervisorTeam))
              }
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
            >
              <option value="">
                {isSupervisorScoped
                  ? loadingSupervisorTeams
                    ? 'Cargando equipo…'
                    : 'Seleccionar compañero…'
                  : 'Seleccionar empleado…'}
              </option>
              {selectableEmployees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {getEmployeeDisplayName(emp)}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="min-w-[120px]">
          <label htmlFor="hours-year" className="block text-xs text-gray-400 mb-1">
            Año
          </label>
          <select
            id="hours-year"
            value={year}
            onChange={(e) => handleYearChange(Number(e.target.value))}
            disabled={!employeeId}
            className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

        {!isViewer && !isSupervisorScoped && (
          <button
            type="button"
            onClick={() => generateMutation.mutate()}
            disabled={!employeeId || generateMutation.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 hover:bg-blue-500 text-white rounded-lg disabled:opacity-50"
          >
            {generateMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Regenerar resúmenes {year}
          </button>
        )}
      </div>

      {queryError && (
        <div className="mb-4 flex items-center gap-2 text-red-400 bg-red-900/20 border border-red-800 rounded-lg p-4">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>{queryError.message || 'Error al cargar el resumen anual.'}</span>
        </div>
      )}

      {actionError && (
        <div className="mb-4 flex items-center gap-2 text-red-400 bg-red-900/20 border border-red-800 rounded-lg p-4">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>{actionError}</span>
        </div>
      )}

      {!employeeId && factoryId && !isViewer && (
        <div className="text-gray-400 text-center py-16 border border-dashed border-gray-600 rounded-lg">
          {isSupervisorScoped
            ? 'Selecciona un compañero de tu equipo para ver su control de horas anual.'
            : 'Selecciona un empleado para ver su control de horas anual.'}
        </div>
      )}

      {employeeId && loadingSummary && (
        <div className="flex justify-center py-12 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Cargando resumen…
        </div>
      )}

      {employeeId && !loadingSummary && summary && selectedMonth == null && (
        <>
          {selectedEmployee && (
            <p className="text-sm text-gray-300 mb-4">
              Empleado:{' '}
              <span className="font-medium text-white">
                {getEmployeeDisplayName(selectedEmployee)}
              </span>{' '}
              · Año {year}
            </p>
          )}

          {hasMissingMonths && !isViewer && !isSupervisorScoped && (
            <div className="mb-4 flex items-start gap-2 text-amber-300 bg-amber-900/20 border border-amber-700/50 rounded-lg p-4 text-sm">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
              <span>
                Faltan resúmenes para {missingMonths.length} mes
                {missingMonths.length === 1 ? '' : 'es'}. Se generarán automáticamente el día 2 del
                mes siguiente, o puedes pulsar «Regenerar resúmenes» para calcularlos ahora a partir
                de calendario, asistencia y ausencias aprobadas.
              </span>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
            <KPI label="Días laborables" value={totals?.working_days ?? 0} />
            <KPI label="Festivos" value={totals?.holiday_days ?? 0} />
            <KPI label="Vacaciones" value={totals?.vacation_days ?? 0} />
            <KPI label="Otras ausencias" value={totals?.absence_days ?? 0} />
            <KPI label="Horas teóricas" value={formatHours(totals?.theoretical_hours)} />
            <KPI label="Horas reales" value={formatHours(totals?.actual_hours)} />
            <KPI label="Horas extra" value={formatHours(totals?.overtime_hours)} />
          </div>

          <p className="text-xs text-gray-500 mb-2">Haz clic en un mes para ver el detalle diario.</p>

          <div className="overflow-x-auto rounded-lg border border-gray-700">
            <table className="min-w-full text-sm text-left text-gray-200">
              <thead className="bg-gray-800 text-gray-300">
                <tr>
                  <th className="px-4 py-3 font-medium">Mes</th>
                  <th className="px-4 py-3 font-medium text-right">Lab.</th>
                  <th className="px-4 py-3 font-medium text-right">Fest.</th>
                  <th className="px-4 py-3 font-medium text-right">Vac.</th>
                  <th className="px-4 py-3 font-medium text-right">Aus.</th>
                  <th className="px-4 py-3 font-medium text-right">Teóricas</th>
                  <th className="px-4 py-3 font-medium text-right">Reales</th>
                  <th className="px-4 py-3 font-medium text-right">Extra</th>
                  <th className="px-4 py-3 w-8" aria-hidden />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {monthlyRows.map((row) => (
                  <tr
                    key={row.month}
                    onClick={() => handleMonthSelect(row.month)}
                    className={`cursor-pointer transition-colors hover:bg-blue-900/25 ${
                      row.hasData ? 'bg-gray-900/40' : 'bg-gray-900/20'
                    }`}
                    title={`Ver detalle de ${row.label}`}
                  >
                    <td className="px-4 py-2.5 text-white font-medium">{row.label}</td>
                    <td className="px-4 py-2.5 text-right text-gray-200">
                      {formatCellDays(row.working_days)}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-200">
                      {formatCellDays(row.holiday_days)}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-200">
                      {formatCellDays(row.vacation_days)}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-200">
                      {formatCellDays(row.absence_days)}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-200">
                      {row.theoretical_hours != null ? formatHours(row.theoretical_hours) : '—'}
                    </td>
                    <td
                      className={`px-4 py-2.5 text-right ${
                        row.actual_hours > 0 ? 'text-blue-300 font-medium' : 'text-gray-200'
                      }`}
                    >
                      {row.actual_hours != null ? formatHours(row.actual_hours) : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-200">
                      {row.overtime_hours != null ? formatHours(row.overtime_hours) : '—'}
                    </td>
                    <td className="px-2 py-2.5 text-gray-500">
                      <ChevronRight className="w-4 h-4" />
                    </td>
                  </tr>
                ))}
              </tbody>
              {totals && (
                <tfoot className="bg-gray-800 font-semibold text-white">
                  <tr>
                    <td className="px-4 py-3">Total {year}</td>
                    <td className="px-4 py-3 text-right">{totals.working_days}</td>
                    <td className="px-4 py-3 text-right">{totals.holiday_days}</td>
                    <td className="px-4 py-3 text-right">{totals.vacation_days}</td>
                    <td className="px-4 py-3 text-right">{totals.absence_days}</td>
                    <td className="px-4 py-3 text-right">
                      {formatHours(totals.theoretical_hours)}
                    </td>
                    <td className="px-4 py-3 text-right text-blue-300">
                      {formatHours(totals.actual_hours)}
                    </td>
                    <td className="px-4 py-3 text-right">{formatHours(totals.overtime_hours)}</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {(fetchingSummary || generateMutation.isPending) && (
            <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" />
              Actualizando datos…
            </p>
          )}
        </>
      )}

      {employeeId && selectedMonth != null && (
        <ControlHorasMonthDetail
          employeeName={selectedEmployee ? getEmployeeDisplayName(selectedEmployee) : ''}
          year={year}
          month={selectedMonth}
          detail={monthDetail}
          loading={loadingMonthDetail}
          error={monthDetailError}
          onBack={handleMonthBack}
        />
      )}
    </div>
  );
};

export default ControlHoras;
